import { __assign, __read, __rest, __spread } from "tslib";
import { base64ToUnicode, logger } from '@sentry/utils';
/** Extract sdk info from from the API metadata */
function getSdkMetadataForEnvelopeHeader(api) {
    if (!api.metadata || !api.metadata.sdk) {
        return;
    }
    var _a = api.metadata.sdk, name = _a.name, version = _a.version;
    return { name: name, version: version };
}
/**
 * Apply SdkInfo (name, version, packages, integrations) to the corresponding event key.
 * Merge with existing data if any.
 **/
function enhanceEventWithSdkInfo(event, sdkInfo) {
    if (!sdkInfo) {
        return event;
    }
    event.sdk = event.sdk || {
        name: sdkInfo.name,
        version: sdkInfo.version,
    };
    event.sdk.name = event.sdk.name || sdkInfo.name;
    event.sdk.version = event.sdk.version || sdkInfo.version;
    event.sdk.integrations = __spread((event.sdk.integrations || []), (sdkInfo.integrations || []));
    event.sdk.packages = __spread((event.sdk.packages || []), (sdkInfo.packages || []));
    return event;
}
/**
 * Create a SentryRequest from an error, message, or transaction event.
 *
 * @param event The event to send
 * @param api Helper to provide the correct url for the request
 * @returns SentryRequest representing the event
 */
export function eventToSentryRequest(event, api) {
    var sdkInfo = getSdkMetadataForEnvelopeHeader(api);
    var eventWithSdkInfo = sdkInfo ? enhanceEventWithSdkInfo(event, api.metadata.sdk) : event;
    if (event.type === 'transaction') {
        return transactionToSentryRequest(eventWithSdkInfo, api);
    }
    return {
        body: JSON.stringify(eventWithSdkInfo),
        type: event.type || 'event',
        url: api.getStoreEndpointWithUrlEncodedAuth(),
    };
}
/**
 * Create a SentryRequest from a transaction event.
 *
 * Since we don't need to manipulate envelopes nor store them, there is no exported concept of an Envelope with
 * operations including serialization and deserialization. Instead, we only implement a minimal subset of the spec to
 * serialize events inline here. See https://develop.sentry.dev/sdk/envelopes/.
 *
 * @param event The transaction event to send
 * @param api Helper to provide the correct url for the request
 * @returns SentryRequest in envelope form
 */
export function transactionToSentryRequest(event, api) {
    var sdkInfo = getSdkMetadataForEnvelopeHeader(api);
    var _a = event.debug_meta || {}, transactionSampling = _a.transactionSampling, tracestate = _a.tracestate, metadata = __rest(_a, ["transactionSampling", "tracestate"]);
    var _b = transactionSampling || {}, samplingMethod = _b.method, sampleRate = _b.rate;
    if (Object.keys(metadata).length === 0) {
        delete event.debug_meta;
    }
    else {
        event.debug_meta = metadata;
    }
    // the tracestate is stored in bas64-encoded JSON, but envelope header values are expected to be full JS values,
    // so we have to decode and reinflate it
    var reinflatedTracestate;
    try {
        // Because transaction metadata passes through a number of locations (transactionContext, transaction, event during
        // processing, event as sent), each with different requirements, all of the parts are typed as optional. That said,
        // if we get to this point and either `tracestate` or `tracestate.sentry` are undefined, something's gone very wrong.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        var encodedSentryValue = tracestate.sentry.replace('sentry=', '');
        reinflatedTracestate = JSON.parse(base64ToUnicode(encodedSentryValue));
    }
    catch (err) {
        logger.warn(err);
    }
    var envelopeHeaders = JSON.stringify(__assign(__assign({ event_id: event.event_id, sent_at: new Date().toISOString() }, (sdkInfo && { sdk: sdkInfo })), (reinflatedTracestate && { trace: reinflatedTracestate })));
    var itemHeaders = JSON.stringify({
        type: event.type,
        // TODO: Right now, sampleRate will be undefined in the cases of inheritance and explicitly-set sampling decisions.
        sample_rates: [{ id: samplingMethod, rate: sampleRate }],
    });
    var req = {
        // The trailing newline is optional; leave it off to avoid sending unnecessary bytes.
        // body: `${envelopeHeaders}\n${itemHeaders}\n${JSON.stringify(event)\n}`,
        body: envelopeHeaders + "\n" + itemHeaders + "\n" + JSON.stringify(event),
        type: 'transaction',
        url: api.getEnvelopeEndpointWithUrlEncodedAuth(),
    };
    return req;
}
/**
 * Create a SentryRequest from a session event.
 *
 * @param event The session event to send
 * @param api Helper to provide the correct url for the request
 * @returns SentryRequest in envelope form
 */
export function sessionToSentryRequest(session, api) {
    var sdkInfo = getSdkMetadataForEnvelopeHeader(api);
    var envelopeHeaders = JSON.stringify(__assign({ sent_at: new Date().toISOString() }, (sdkInfo && { sdk: sdkInfo })));
    var itemHeaders = JSON.stringify({
        type: 'session',
    });
    return {
        body: envelopeHeaders + "\n" + itemHeaders + "\n" + JSON.stringify(session),
        type: 'session',
        url: api.getEnvelopeEndpointWithUrlEncodedAuth(),
    };
}
//# sourceMappingURL=request.js.map